import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
    LinearProgress,
    styled,
    useTheme,
    Card,
    CardContent,
    CardMedia,
    Button,
    Typography,
    MobileStepper,
} from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";
// Axios Cache Instance
import {axiosCached} from "../../../../redux/auth/authService";
import NoImage from "../../../../styles/images/NoProjectImageFound.jpg";


const PREFIX = 'ImageCard';

const classes = {
    card: `${PREFIX}-card`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardMediaLoading: `${PREFIX}-cardMediaLoading`,
    loadingContainer: `${PREFIX}-loadingContainer`,
    media: `${PREFIX}-media`,
    cardHeader: `${PREFIX}-cardHeader`,
    cardHeaderAvatar: `${PREFIX}-cardHeaderAvatar`,
    cardHeaderTitle: `${PREFIX}-cardHeaderTitle`,
    cardHeaderSubtitle: `${PREFIX}-cardHeaderSubtitle`,
    cardContent: `${PREFIX}-cardContent`,
    avatar: `${PREFIX}-avatar`,
    date: `${PREFIX}-date`,
    description: `${PREFIX}-description`
};

const StyledCard = styled(Card)((
    {
        theme
    }
) => ({
    [`&.${classes.card}`]: {
		width: '100%',
		height: '100%'
		// padding: '5px'
	},

    [`& .${classes.linearProgress}`]: {
		height: '1px'
	},

    [`& .${classes.cardMediaLoading}`]: {
		position: 'relative',
		width: '100%',
		// height: 'calc(100% - 53px - 68px - 40px)',
		height: '114px'
	},

    [`& .${classes.loadingContainer}`]: {
		width: '100%',
		// height: '100%',
		height: '114px',
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		margin: '0px'
	},

    [`& .${classes.media}`]: {
		height: '325px',
		marginTop: '1px',
		backgroundColor: '#FFF'
	},

    [`& .${classes.cardHeader}`]: {
		position: '-webkit-sticky',
		position: 'sticky',
		top: '0px',
	},

    [`& .${classes.cardHeaderAvatar}`]: {},

    [`& .${classes.cardHeaderTitle}`]: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

    [`& .${classes.cardHeaderSubtitle}`]: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

    [`& .${classes.cardContent}`]: {
		paddingTop: '0px'
	},

    [`& .${classes.avatar}`]: {
	},

    [`& .${classes.date}`]: {
		// TODO: Center up with css
		marginLeft: '170px',
	},

    [`& .${classes.description}`]: {
		paddingTop: '20px',
	}
}));

export default function ImageCard({configuration, onLoading, onLoaded}) {


	const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';
	const imageSize = configuration.element?.imageSize ? configuration.element?.imageSize : 'smImage';
	// const imageHeight = configuration.element?.imageHeight ? configuration.element?.imageHeight : '115px';
	const card = configuration.element?.card ? configuration.element?.card : null;

	// App State
	const auth = useSelector(state => state.auth);
	const config = useSelector(state => state.config);
	const data = useSelector(state => state.data);
	const dataSource = useDataSource(dataSourceName);

	// Component State
	const [imageData, setImageData] = useState(null);
	const [activeStep, setActiveStep] = useState(null);
	const [image, setImage] = useState(null);
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [projectImageLoading, setImageLoading] = useState(true);
	const [maxSteps, setMaxSteps] = useState(dataSource.data ? dataSource.data.length : 0);

	// Loading
	useEffect(() => {
		if (dataSource.fetching === true) {
			setImage(null);

			if (isFunction(onLoading)) {
				onLoading();
			}
		}
	}, [dataSource.fetching]);

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {
			if (isFunction(onLoaded)) {
				onLoaded();
			}
		}
	}, [dataSource.fetched]);

	useEffect(() => {
		if (dataSource.data && dataSource.data.length > 0) {
			setImageData(dataSource.data);
			setMaxSteps(dataSource.data.length);
			setActiveStep(0);
		}
	}, [dataSource.data]);

	useEffect(() => {
		if ((typeof activeStep === 'number') && dataSource.data) {
			loadStep();
		}
	},[activeStep, imageData])

	// Get images
	const loadStep = () => {
		const nextStep = activeStep;
		const url = config.data.authentication.api + 'DBQuery';

		setImageLoading(true);
		axiosCached.get(url, {
			params: {
				db: config.data.authentication.apiSource,
				table: 'ProjectImage{0}'.format(data.viewStaged ? 'Staged': ''),
				fields: "DefaultImage," + imageSize,
				where: 'ProjectImage{0}ID'.format(data.viewStaged ? 'Staged': '') + " eq '" + imageData[nextStep].projectimageid + "'"
			},
			useCache: true
		})
		.then(function (response) {
			let defaultImage = response.data.find(i => {
				return i.defaultImage === true
			})
			if (!defaultImage) {
				defaultImage = response.data[0];
			}
			const imageBase64 = 'data:image/png;base64,' + defaultImage[imageSize];
			setImage({img: imageBase64});
		})
		.catch(function (error) {
			console.log('**ERROR**', error);
			setImage(NoImage);
		});
	}

	// Set projectImageLoading to false once image value is returned
	useEffect(() => {
		if (image) {
			setImageLoading(false);
		} else {
			setImageLoading(true);
		}
	}, [image]);

	const handleNext = () => {
		// activeStep = activeStep + 1;
		// loadStep(1);
		setActiveStep(activeStep + 1);
	}

	const handleBack = () => {
		// activeStep = activeStep - 1;
		// loadStep(-1);
		setActiveStep(activeStep - 1);
	}

	function isFunction(functionToCheck) {
		return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
	}

	return (
        <StyledCard className={classes.card} style={{...card}}>
			{showProgress && dataSource.fetching &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{showProgress && !dataSource.fetching &&
				<div className={classes.linearProgress}/>
			}
			<CardContent className={classes.cardContent} style={{padding: '0px', height: '75%'}}>
				{showProgress && projectImageLoading && dataSource.fetched &&
					<LinearProgress className={classes.linearProgress}/>
				}
				{showProgress && !projectImageLoading &&
					<div className={classes.linearProgress}/>
				}
				{image &&
					<CardMedia className={classes.media} style={{height: '100%'}} image={image.img} title={imageData[0].id}/>
				}
				{!image && !dataSource.fetching && !dataSource.data &&
					<Typography variant="subtitle2" gutterBottom style={{height: '100%', textAlign: 'center'}}>
						No image...
					</Typography>
				}
			</CardContent>
			{dataSource.fetched &&
				<MobileStepper
					style={{height: '25%'}}
					steps={maxSteps}
					position="static"
					activeStep={activeStep}
					variant="text"
					// className={classes.mobileStepper}
					// classes={{root:classes.mobileStepperRoot}}
					nextButton={
						<Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
							<KeyboardArrowRight/>
						</Button>
					}
					backButton={
						<Button size="small" onClick={handleBack} disabled={activeStep === 0}>
							<KeyboardArrowLeft/>
						</Button>
					}
				/>
			}
		</StyledCard>
    );
}
